import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import { cx } from './primitives'

export type ButtonVariant = 'primary' | 'secondary' | 'lime' | 'ghost' | 'subtle' | 'danger' | 'danger-soft' | 'warning' | 'warning-soft'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface CommonProps {
  variant?: ButtonVariant
  size?: ButtonSize
  full?: boolean
  loading?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
}

function classes({ variant = 'primary', size = 'md', full }: CommonProps, extra?: string) {
  return cx('ui-btn', `ui-btn--${variant}`, size !== 'md' && `ui-btn--${size}`, full && 'ui-btn--full', extra)
}

function Inner({ loading, leftIcon, rightIcon, children }: CommonProps & { children?: ReactNode }) {
  return (
    <>
      {loading ? <span className="ui-btn__spinner" aria-hidden="true" /> : leftIcon}
      {children}
      {rightIcon}
    </>
  )
}

export type ButtonProps = CommonProps & ButtonHTMLAttributes<HTMLButtonElement>

/** Tasarım kitinin düğmesi. Varsayılan birincil (mürekkep), 40 px. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, full, loading, leftIcon, rightIcon, className, children, disabled, type = 'button', ...rest }, ref,
) {
  return (
    <button ref={ref} type={type} className={classes({ variant, size, full }, className)}
      disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      <Inner loading={loading} leftIcon={leftIcon} rightIcon={rightIcon}>{children}</Inner>
    </button>
  )
})

/** Düğme görünümlü uygulama içi bağlantı. */
export const ButtonLink = forwardRef<HTMLAnchorElement, CommonProps & LinkProps>(function ButtonLink(
  { variant, size, full, leftIcon, rightIcon, className, children, ...rest }, ref,
) {
  return (
    <Link ref={ref} className={classes({ variant, size, full }, className)} {...rest}>
      <Inner leftIcon={leftIcon} rightIcon={rightIcon}>{children}</Inner>
    </Link>
  )
})

/** Düğme görünümlü dış bağlantı (indirme vb.). */
export const ButtonAnchor = forwardRef<HTMLAnchorElement, CommonProps & AnchorHTMLAttributes<HTMLAnchorElement>>(function ButtonAnchor(
  { variant, size, full, leftIcon, rightIcon, className, children, ...rest }, ref,
) {
  return (
    <a ref={ref} className={classes({ variant, size, full }, className)} {...rest}>
      <Inner leftIcon={leftIcon} rightIcon={rightIcon}>{children}</Inner>
    </a>
  )
})

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Yalnız simgeli düğmenin erişilebilir adı — zorunlu. */
  label: string
  size?: 'sm' | 'md' | 'lg'
  tone?: 'default' | 'danger'
  active?: boolean
  outline?: boolean
  /** Varsayılan: `label` aynı zamanda ipucu (title) olur. */
  noTitle?: boolean
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, size = 'md', tone = 'default', active, outline, noTitle, className, type = 'button', ...rest }, ref,
) {
  return (
    <button ref={ref} type={type} aria-label={label} title={noTitle ? undefined : label}
      className={cx('ui-icon-btn', size !== 'md' && `ui-icon-btn--${size}`, tone === 'danger' && 'ui-icon-btn--danger',
        active && 'ui-icon-btn--active', outline && 'ui-icon-btn--outline', className)}
      {...rest} />
  )
})

/** Simgeli bağlantı (ör. satırdaki "Düzenle"). */
export const IconLink = forwardRef<HTMLAnchorElement, LinkProps & { label: string; size?: 'sm' | 'md' | 'lg' }>(function IconLink(
  { label, size = 'md', className, ...rest }, ref,
) {
  return <Link ref={ref} aria-label={label} title={label} className={cx('ui-icon-btn', size !== 'md' && `ui-icon-btn--${size}`, className)} {...rest} />
})
